import React, { useMemo } from 'react';
import { Box } from '@mui/material';

import type { TinyPreviewCardProps } from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import {
    noLinkImagePath,
    noAdImagePath,
    noProjectImagePath,
    noNewsPostImagePath,
    defaultAudioImagePath,
    noImagePath,
} from 'src/constants/defaultImages';
import { styledComponent, styledTag } from 'src/utils/styled';
import Avatar from 'src/components/ui/Avatar';
import { colours } from 'src/constants/colours';
import Image from 'src/components/ui/Image';
import getMediaThumbnail from 'src/utils/getMediaThumbnail';
import getMediaInfo from 'src/utils/getMediaInfo';
import { MediumType } from 'src/types/__generated__/graphql';

const ImageContainer = styledComponent(Box)`
    display: flex;
    width: max-content;
    border-radius: 0.5rem 0 0 0.5rem;
    overflow: hidden;
`;

const StyledImage = styledComponent(Image)`
    object-fit: cover;
`;

const StyledVideo = styledTag('video')`
    width: 6rem;
    height: 6rem;
    object-fit: cover;
    border-radius: 0.5rem 0 0 0.5rem;
`;

interface TinyPreviewCardMediaProps {
    media: TinyPreviewCardProps['media'];
    mediaUrl?: TinyPreviewCardProps['mediaUrl'];
    type: TinyPreviewCardProps['type'];
    fallbackText?: string | null;
}

const TinyPreviewCardMedia: React.FC<TinyPreviewCardMediaProps> = ({ media, mediaUrl, type, fallbackText }) => {
    const mediaToDisplayIfEmptyMedia = useMemo(() => {
        switch (type) {
            case 'ad':
                return noAdImagePath;
            case 'project':
                return noProjectImagePath;
            case 'newsPost':
                return noNewsPostImagePath;
            default:
                return noImagePath;
        }
    }, [type]);

    const mediaContent = useMemo(() => {
        const mediaThumbnail = media ? getMediaThumbnail(media) : null;
        const mediaInfo = mediaThumbnail?.url ? getMediaInfo(mediaThumbnail.url) : undefined;

        switch (type) {
            case 'link':
                return (
                    <ImageContainer>
                        <StyledImage
                            width={96}
                            height={96}
                            src={mediaUrl ?? noLinkImagePath}
                            alt={mediaUrl ?? 'image url'}
                            shouldUseImgTag
                        />
                    </ImageContainer>
                );
            case 'user':
            case 'company':
                return (
                    <Avatar
                        image={mediaThumbnail ?? undefined}
                        size={40}
                        sx={{ ml: '1rem' }}
                        data-target-id="tinyPreviewCard-avatar"
                        fallbackText={fallbackText}
                        isCompany={type === 'company'}
                    />
                );
            case 'pagesJaunesPro':
                return (
                    <Avatar
                        image={mediaThumbnail ?? undefined}
                        size={40}
                        sx={{ ml: '1rem' }}
                        data-target-id="tinyPreviewCard-avatar"
                        badgeIcon="PagesJaunes"
                        badgeSize="1rem"
                        badgeColor="transparent"
                        badgeBgColor={colours.white.main}
                        badgeMargin="0 -0.3rem -0.15rem 0"
                        badgeBorderColor={colours.white.main}
                        badgeBorderRadius="0.25rem"
                        badgeBorderWidth="2px"
                        isCompany
                    />
                );
            default: {
                let inner;

                if (mediaInfo) {
                    switch (mediaInfo.type) {
                        case MediumType.Video:
                            inner = (
                                <StyledVideo muted>
                                    <source src={mediaThumbnail?.url ?? mediaToDisplayIfEmptyMedia} />
                                </StyledVideo>
                            );
                            break;
                        case MediumType.Audio:
                            inner = (
                                <StyledImage alt="preview-audio" src={defaultAudioImagePath} width={96} height={96} />
                            );
                            break;
                        default:
                            inner = (
                                <StyledImage
                                    alt="preview-image"
                                    src={mediaThumbnail?.url ?? mediaToDisplayIfEmptyMedia}
                                    width={96}
                                    height={96}
                                />
                            );
                    }
                    // Post can have no media
                } else if (type !== 'post') {
                    inner = (
                        <StyledImage
                            alt="preview-image"
                            src={mediaThumbnail?.url ?? mediaToDisplayIfEmptyMedia}
                            width={96}
                            height={96}
                        />
                    );
                }

                return inner ? <ImageContainer>{inner}</ImageContainer> : null;
            }
        }
    }, [fallbackText, media, mediaToDisplayIfEmptyMedia, mediaUrl, type]);

    return (
        <Box display="flex" alignItems="center" data-target-id="tinyPreviewCard-media">
            {mediaContent}
        </Box>
    );
};

export default TinyPreviewCardMedia;
